import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import type { Country } from "react-phone-number-input";
import { paymentMethodsQueryOptions } from "@/lib/payment-methods";
import { websiteConfigQueryOptions } from "@/lib/website-config";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  CheckIcon,
  ClockIcon,
  MapPinIcon,
  StickyNoteIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import z from "zod";
import {
  OrderTypes,
  PaymentMethodContext,
  PaymentMethods,
  SupportedLanguages,
} from "@/lib/supabase/enums";
import { PaymentMethodCard } from "@/components/payment-method-card";
import { getPaymentMethodFee } from "@/lib/payment-method-config";
import { CheckoutRow } from "@/components/checkout/checkout-row";
import { CheckoutSection } from "@/components/checkout/checkout-section";
import { useApplePayAvailable } from "@/hooks/use-apple-pay-available";
import { getSessionId } from "@/lib/session-cache";
import LoadingSpinner from "@/components/loading-spinner";
import { useFormContext, useWatch } from "react-hook-form";
import { cn, formatNumberToCurrency, getDeliveryFee } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { useCartStore } from "@/components/cart/store";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createOrder, createOrderSchema } from "@/lib/orders";
import { hasKnownEmailTld } from "@vrodex/utils/email-typo";
import { EmailSuggestion } from "@/components/email-suggestion";
import { useEmailSuggestion } from "@/hooks/use-email-suggestion";
import { useCustomerStore } from "@/components/customer/store";
import { useAlert } from "@/hooks/use-alert";
import { useCheckDeliveryArea } from "@/hooks/use-check-delivery-areay";
import { deliveryAreaQueryOptions } from "@/lib/delivery-areas";
import {
  Availability,
  availabilityQueryOptions,
  hasDeliveryShiftsQueryOptions,
} from "@/lib/shifts";
import { getAsapStartWhenClosed } from "@/lib/asap-start";
import { intlFormat } from "date-fns";
import { useGetTranslatedOrderType } from "@/hooks/use-get-translated-order-type";
import { useEnsureValidOrderType } from "@/hooks/use-has-delivery-shifts";
import { useShallow } from "zustand/react/shallow";
import Decimal from "decimal.js";
import { useMenuQuery } from "@/hooks/use-menu-query";
import type { Product } from "@/lib/products";
import { productsQueryOptions } from "@/lib/products";
import { SalesChannels } from "@/lib/storefront/channel";
import { shippingMethodsQueryOptions } from "@/lib/storefront/shipping";
import { StorefrontCheckout } from "@/components/storefront/checkout/storefront-checkout";
import { checkCoupon } from "@/lib/coupons";
import { TagIcon, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

const TODAY = new Date();

// Helper function to calculate total option price for an item
const getSelectedOptionsTotalPrice = (
  product: Product | undefined,
  selectedOptions: Record<string, string> | undefined,
): number => {
  if (!product || !selectedOptions) return 0;

  let total = 0;

  product.productOptionSubgroups?.forEach((subgroup) => {
    const selectedOptionValue = selectedOptions[subgroup.id];
    if (selectedOptionValue) {
      // Handle comma-separated option IDs (for optional checkboxes)
      const optionIds = selectedOptionValue.split(",");
      optionIds.forEach((optionId) => {
        const option = subgroup.productOptions?.find(
          (opt) => opt.id === optionId.trim(),
        );
        if (option) {
          total += option.price;
        }
      });
    }
  });

  return total;
};

// Resolve the display names of the options a customer selected for an item so
// the order-summary dialog can list them under each line.
const getSelectedOptionNames = (
  product: Product | undefined,
  selectedOptions: Record<string, string> | undefined,
): string[] => {
  if (!product || !selectedOptions) return [];

  const names: string[] = [];

  product.productOptionSubgroups?.forEach((subgroup) => {
    const selectedOptionValue = selectedOptions[subgroup.id];
    if (selectedOptionValue) {
      selectedOptionValue.split(",").forEach((optionId) => {
        const option = subgroup.productOptions?.find(
          (opt) => opt.id === optionId.trim(),
        );
        if (option) {
          names.push(option.name);
        }
      });
    }
  });

  return names;
};

// Helper function to filter enabled and available items
const getEnabledAndAvailableItems = (
  items: Array<{ productId: string; enabled?: boolean }>,
  productsByCategory: Array<{ products: Product[] }> | undefined,
) => {
  return items.filter((item) => {
    // Check if item is enabled (visible for current order type)
    const isItemEnabled = item.enabled !== false;

    // Check if product is available today
    const product = productsByCategory
      ?.flatMap((category) => category.products)
      .find((p) => p.id === item.productId);
    const isAvailableToday = product?.isAvailableToday ?? true;

    // Item must be both enabled and available today
    return isItemEnabled && isAvailableToday;
  });
};

// Apple Pay is the only method whose availability depends on the browser, and
// both the summary and the selector have to agree on it.
const isPaymentMethodAvailable = (
  paymentMethod: { type: PaymentMethods },
  isApplePayAvailable: boolean,
) =>
  paymentMethod.type === PaymentMethods.applepay ? isApplePayAvailable : true;

// Banknote amounts a customer can hand the driver, so they can bring change.
const getCashOptions = (total: number) => {
  const firstOption = Math.ceil(total / 10) * 10; // Round up to nearest 10

  // If the first option equals the total, start from the next option to avoid duplication
  const startAmount = firstOption === total ? firstOption + 10 : firstOption;

  return [
    startAmount,
    startAmount + 10,
    startAmount + 20,
    startAmount + 30,
    startAmount + 50,
  ];
};

export const Route = createFileRoute("/$locale/checkout/")({
  component: CheckoutRoute,
  loader: async ({ context }) => {
    const websiteConfig = await context.queryClient.ensureQueryData(
      websiteConfigQueryOptions(),
    );

    // The webshop's checkout ships or hands over at the shop: no time slots.
    if (websiteConfig.channel === SalesChannels.storefront) {
      return {
        paymentMethods: context.queryClient.ensureQueryData(
          paymentMethodsQueryOptions(),
        ),
        shippingMethods: context.queryClient.ensureQueryData(
          shippingMethodsQueryOptions(),
        ),
        products: context.queryClient.ensureQueryData(productsQueryOptions()),
      };
    }

    return {
      paymentMethods: context.queryClient.ensureQueryData(
        paymentMethodsQueryOptions(),
      ),
      availability: context.queryClient.ensureQueryData(
        availabilityQueryOptions(TODAY),
      ),
      hasDeliveryShifts: context.queryClient.ensureQueryData(
        hasDeliveryShiftsQueryOptions(),
      ),
    };
  },
});

/**
 * Move the viewport to whatever the customer still has to fix. Without this a
 * failed submit does nothing visible on a phone, because the offending row sits
 * far above the order button.
 */
function scrollToFirstError() {
  requestAnimationFrame(() => {
    const element = document.querySelector<HTMLElement>(
      '[data-error-anchor="true"], [aria-invalid="true"]',
    );
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    element.focus?.({ preventScroll: true });
  });
}

const RequiredMark = () => (
  <span aria-hidden="true" className="text-red-500">
    *
  </span>
);

/**
 * The order schema is shared with the server, so its Zod messages are English
 * only. Every field therefore renders its own translated message instead.
 */
const FieldError = ({ show, message }: { show: boolean; message: string }) =>
  show ? <p className="text-sm font-medium text-red-600">{message}</p> : null;

const CustomerDataFields = () => {
  //global state
  const { setCustomer } = useCustomerStore(
    useShallow((state) => ({
      setCustomer: state.setCustomer,
    })),
  );

  //translations
  const { t } = useTranslation("translation");

  //queries
  const { data: websiteConfig } = useSuspenseQuery(websiteConfigQueryOptions());

  // Default the phone country selector to the business' own country so most
  // customers get the right dialing code without touching the selector.
  const defaultPhoneCountry =
    (websiteConfig.companyCountry?.toUpperCase() as Country) || "NL";

  //form context (bound directly to the parent checkout form)
  const { control, setValue } =
    useFormContext<z.input<typeof createOrderSchema>>();

  const customerValue = useWatch({ control, name: "customer" });
  const emailSuggestion = useEmailSuggestion(customerValue?.email);

  // Persist the customer details as they are entered so a returning visitor is
  // pre-filled on their next order. The address fields are left untouched.
  useEffect(() => {
    if (!customerValue) return;
    const current = useCustomerStore.getState().customer;
    setCustomer({
      ...current,
      firstName: customerValue.firstName ?? "",
      lastName: customerValue.lastName ?? "",
      phone: customerValue.phone ?? "",
      email: customerValue.email ?? "",
    });
  }, [customerValue, setCustomer]);

  return (
    <div className="flex flex-col gap-4 p-4">
      <FormField
        control={control}
        name="customer.firstName"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>
              {t("Checkout.CustomerData.first_name")} <RequiredMark />
            </FormLabel>
            <FormControl>
              <Input
                placeholder={t("Checkout.CustomerData.first_name_placeholder")}
                {...field}
              />
            </FormControl>
            <FieldError
              show={!!fieldState.error}
              message={t("Checkout.CustomerData.first_name_required")}
            />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="customer.lastName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("Checkout.CustomerData.last_name")}{" "}
              <span className="text-muted-foreground font-normal">
                {t("Checkout.optional_suffix")}
              </span>
            </FormLabel>
            <FormControl>
              <Input
                placeholder={t("Checkout.CustomerData.last_name_placeholder")}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="customer.phone"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>
              {t("Checkout.CustomerData.phone")} <RequiredMark />
            </FormLabel>
            <FormControl>
              <PhoneInput
                placeholder={t("Checkout.CustomerData.phone_placeholder")}
                defaultCountry={defaultPhoneCountry}
                {...field}
              />
            </FormControl>
            <FieldError
              show={!!fieldState.error}
              message={t("Checkout.CustomerData.phone_required")}
            />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="customer.email"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>
              {t("Checkout.CustomerData.email")} <RequiredMark />
            </FormLabel>
            <FormControl>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder={t("Checkout.CustomerData.email_placeholder")}
                {...field}
              />
            </FormControl>
            <FieldError
              show={!!fieldState.error}
              message={
                hasKnownEmailTld(field.value ?? "")
                  ? t("Checkout.CustomerData.email_invalid")
                  : t("Checkout.CustomerData.email_unknown_ending")
              }
            />
            <EmailSuggestion
              suggestion={emailSuggestion}
              onAccept={(email) =>
                setValue("customer.email", email, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
          </FormItem>
        )}
      />
    </div>
  );
};

const AddressDataForm = ({
  value,
  onChange,
}: {
  value?: {
    streetName: string;
    streetNumber?: string;
    zipCode?: string;
    city?: string;
  };
  onChange: (data: {
    streetName: string;
    streetNumber?: string;
    zipCode?: string;
    city?: string;
  }) => void;
}) => {
  //global state
  const { updateCustomer, customer } = useCustomerStore(
    useShallow((state) => ({
      updateCustomer: state.updateCustomer,
      customer: state.customer,
    })),
  );

  //translations
  const { t } = useTranslation("translation");

  //hooks
  const { checkDeliveryArea, fetchingAddress } = useCheckDeliveryArea();
  const addressForm = useForm({
    schema: z.object({
      streetName: z.string().min(1),
      streetNumber: z.string().optional(),
      zipCode: z.string().optional(),
      city: z.string().optional(),
      latitude: z.number().optional(),
      longitude: z.number().optional(),
    }),
    defaultValues: {
      streetName: value?.streetName ?? "",
      streetNumber: value?.streetNumber ?? "",
      zipCode: value?.zipCode ?? "",
      city: value?.city ?? "",
    },
  });

  const handleSubmit = addressForm.handleSubmit((data) => {
    onChange(data);
    updateCustomer({
      ...customer,
      streetName: data.streetName,
      streetNumber: data.streetNumber ?? "",
      zipCode: data.zipCode ?? "",
      city: data.city ?? "",
    });
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit();
  };

  const handleFetchAddress = async (
    postalCode?: string,
    streetNumber?: string,
  ) => {
    if (!postalCode || !streetNumber) {
      return;
    }

    addressForm.clearErrors("streetName");

    const result = await checkDeliveryArea(postalCode, streetNumber);
    if (result?.address) {
      addressForm.setValue("streetName", result.address.street);
      addressForm.setValue("city", result.address.city);
      addressForm.setValue("latitude", result.address.latitude);
      addressForm.setValue("longitude", result.address.longitude);
    } else {
      if (result?.code === "address_not_in_delivery_area") {
        addressForm.setError("streetName", {
          message: t(
            "Checkout.Errors.address_not_in_delivery_area.description",
          ),
        });
      } else if (result?.code === "delivery_area_unavailable_today") {
        addressForm.setError("streetName", {
          message: t(
            "Checkout.Errors.delivery_area_unavailable_today.description",
          ),
        });
      } else if (result?.code === "address_not_valid") {
        addressForm.setError("streetName", {
          message: t("DeliveryArea.Errors.address_not_valid.description"),
        });
      }
    }
  };

  return (
    <Form {...addressForm}>
      {fetchingAddress && (
        <LoadingSpinner
          text={t("Checkout.AddressData.fetching_address")}
          type="moon"
        />
      )}
      <form
        id="address-data"
        onSubmit={handleFormSubmit}
        className="grid grid-cols-2 gap-4"
      >
        <FormField
          control={addressForm.control}
          name="zipCode"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Checkout.AddressData.postal_code")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onBlur={() => {
                    handleFetchAddress(
                      field.value,
                      addressForm.getValues("streetNumber"),
                    );
                  }}
                  placeholder={t(
                    "Checkout.AddressData.postal_code_placeholder",
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={addressForm.control}
          name="streetNumber"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Checkout.AddressData.street_number")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t(
                    "Checkout.AddressData.street_number_placeholder",
                  )}
                  {...field}
                  onBlur={() => {
                    handleFetchAddress(
                      addressForm.getValues("zipCode"),
                      field.value,
                    );
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={addressForm.control}
          name="streetName"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Checkout.AddressData.streetName")}</FormLabel>
              <FormControl>
                <Input disabled {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={addressForm.control}
          name="city"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Checkout.AddressData.city")}</FormLabel>
              <FormControl>
                <Input disabled {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="col-span-full">
          <button
            type="submit"
            className="rounded-full w-full bg-black/90 cursor-pointer hover:bg-black flex-1 py-2 px-4 text-white font-bold text-lg"
          >
            {t("save")}
          </button>
        </div>
      </form>
    </Form>
  );
};

const AddressDataDialog = ({
  value,
  onChange,
  hasError = false,
}: {
  value?: {
    streetName: string;
    streetNumber?: string;
    zipCode?: string;
    city?: string;
  };
  onChange: (data: {
    streetName: string;
    streetNumber?: string;
    zipCode?: string;
    city?: string;
  }) => void;
  hasError?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  const { t } = useTranslation("translation");

  const isComplete = !!value?.streetName;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <CheckoutRow
          icon={MapPinIcon}
          // The section header already says "Delivery address", so the row
          // itself leads with the chosen address instead of repeating it.
          label={
            isComplete
              ? [
                  `${value?.streetName ?? ""} ${value?.streetNumber ?? ""}`.trim(),
                  [value?.zipCode, value?.city].filter(Boolean).join(" "),
                ]
                  .filter(Boolean)
                  .join(", ")
              : t("Checkout.AddressData.placeholder")
          }
          isRequired
          isComplete={isComplete}
          errorMessage={
            hasError ? t("Checkout.AddressData.address_required") : undefined
          }
        />
      </DialogTrigger>

      <DialogContent className="overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t("Checkout.AddressData.title")}</DialogTitle>
          <VisuallyHidden>
            <DialogDescription></DialogDescription>
          </VisuallyHidden>
        </DialogHeader>
        <AddressDataForm
          value={value}
          onChange={(data) => {
            onChange(data);
            setOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

/** Slot times are always shown in the business' own 24h notation. */
const formatSlotTime = (date: Date | string) =>
  intlFormat(date, { hour: "2-digit", minute: "2-digit" }, { locale: "nl" });

/**
 * Time picker. Tapping a slot picks it and closes the dialog: the old select
 * plus a separate save button meant three taps for one choice, and it listed
 * sold-out and already-passed slots as greyed out rows that did nothing.
 */
const RequestedTimeForm = ({
  value,
  onChange,
  isLoading,
  firstAvailableSlot,
  availability,
  asapStartsAt,
}: {
  value?: string;
  onChange: (data: string) => void;
  isLoading: boolean;
  firstAvailableSlot?: Availability;
  availability: Availability[];
  /** Set when the service has not started yet, so ASAP means this time. */
  asapStartsAt?: Date;
}) => {
  const { cart } = useCartStore(
    useShallow((state) => ({
      cart: state.cart,
    })),
  );

  const { t } = useTranslation("translation");

  //computed
  const selectableSlots = availability.filter(
    (slot) => slot.type === cart.orderType && slot.spots > 0,
  );

  if (isLoading) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        {t("loading")}
      </p>
    );
  }

  if (!firstAvailableSlot) {
    return (
      <p className="rounded-lg bg-red-50 p-4 text-center text-sm font-medium text-red-600">
        {t("Checkout.RequestedTime.no_slots_available")}
      </p>
    );
  }

  const isAsapSelected = value === "ASAP" || !value;

  return (
    <div className="flex max-h-[60dvh] flex-col gap-4 overflow-y-auto">
      <button
        type="button"
        onClick={() => onChange("ASAP")}
        className={cn(
          "flex min-h-14 w-full cursor-pointer flex-row items-center justify-between gap-3 rounded-lg border p-4 text-start transition-colors",
          isAsapSelected
            ? "border-black bg-black text-white"
            : "hover:bg-gray-100",
        )}
      >
        <span className="flex flex-col">
          <span className="font-bold">{t("Checkout.RequestedTime.asap")}</span>
          {asapStartsAt && (
            <span
              className={cn(
                "text-sm",
                isAsapSelected ? "text-white/80" : "text-muted-foreground",
              )}
            >
              {cart.orderType === OrderTypes.delivery
                ? t("Checkout.RequestedTime.starts_at_delivery", {
                    time: formatSlotTime(asapStartsAt),
                  })
                : t("Checkout.RequestedTime.starts_at_pickup", {
                    time: formatSlotTime(asapStartsAt),
                  })}
            </span>
          )}
        </span>
        {isAsapSelected && <CheckIcon className="size-5 shrink-0" />}
      </button>

      {selectableSlots.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-muted-foreground text-sm font-medium">
            {t("Checkout.RequestedTime.pick_a_time")}
          </span>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {selectableSlots.map((slot) => {
              const slotValue = slot.start.toISOString();
              const isSelected = value === slotValue;

              return (
                <button
                  key={slot.id}
                  type="button"
                  onClick={() => onChange(slotValue)}
                  className={cn(
                    "min-h-11 cursor-pointer rounded-lg border px-2 py-2 text-sm font-medium transition-colors",
                    isSelected
                      ? "border-black bg-black text-white"
                      : "hover:bg-gray-100",
                  )}
                >
                  {formatSlotTime(slot.start)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const RequestedTimeDialog = ({
  value,
  onChange,
  asapStartsAt,
  hasError = false,
}: {
  value?: string;
  onChange: (data: string) => void;
  asapStartsAt?: Date;
  hasError?: boolean;
}) => {
  //global state
  const { cart } = useCartStore(
    useShallow((state) => ({
      cart: state.cart,
    })),
  );

  //local state
  const [open, setOpen] = useState(false);

  //queries
  const availabilityQuery = useQuery(availabilityQueryOptions(TODAY));

  //translations
  const { t } = useTranslation("translation");

  //computed
  const availability = availabilityQuery.data?.data ?? [];

  const isLoading = availabilityQuery.isLoading;

  const firstAvailableSlot = availability.find(
    (availability) =>
      availability.type === cart.orderType && availability.spots > 0,
  );

  const isComplete = !!value && !!firstAvailableSlot;

  // The section header already names the step, so the row leads with the time
  // that was picked rather than repeating "Pickup time" twice. While the service
  // is running ASAP shows no slot time: the kitchen decides when it starts, so
  // naming a time there read as a promise we could not keep. Before it opens
  // (delivery from 16:00, ordered at 14:30) ASAP is that opening time, and
  // leaving it out had customers expecting their order straight away.
  const label = !isComplete
    ? t("Checkout.RequestedTime.placeholder")
    : value === "ASAP"
      ? asapStartsAt
        ? t("Checkout.RequestedTime.asap_from", {
            time: formatSlotTime(asapStartsAt),
          })
        : t("Checkout.RequestedTime.asap")
      : formatSlotTime(value);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <CheckoutRow
          icon={ClockIcon}
          label={label}
          isRequired
          isComplete={isComplete}
          errorMessage={
            !isLoading && !firstAvailableSlot
              ? t("Checkout.RequestedTime.no_slots_available")
              : hasError
                ? t("Checkout.RequestedTime.time_required")
                : undefined
          }
        />
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Checkout.RequestedTime.title")}</DialogTitle>
          <VisuallyHidden>
            <DialogDescription></DialogDescription>
          </VisuallyHidden>
        </DialogHeader>
        <RequestedTimeForm
          isLoading={isLoading}
          firstAvailableSlot={firstAvailableSlot}
          availability={availability}
          asapStartsAt={asapStartsAt}
          value={value}
          onChange={(data) => {
            onChange(data);
            setOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

/**
 * Payment methods now render straight on the page instead of behind a dialog
 * row. Hiding them one tap deep was the single biggest reason orders reached
 * the submit button with no payment method selected.
 */
const PaymentMethodSelector = ({
  value,
  onChange,
  total,
  feeBase,
  hasError = false,
}: {
  value?: { paymentMethodId?: string; paysWith?: string };
  onChange: (data: { paymentMethodId: string; paysWith?: string }) => void;
  total: number;
  // Order before any payment fee, so each card can show what its own fixed
  // plus percentage surcharge actually comes down to.
  feeBase: number;
  hasError?: boolean;
}) => {
  //global state
  const { cart } = useCartStore(
    useShallow((state) => ({
      cart: state.cart,
    })),
  );

  //queries
  const { data: paymentMethods } = useSuspenseQuery(
    paymentMethodsQueryOptions(),
  );

  //translations
  const { t } = useTranslation("translation");

  //hooks
  const isApplePayAvailable = useApplePayAvailable();

  //computed
  const selectablePaymentMethods = paymentMethods
    .filter((paymentMethod) =>
      isPaymentMethodAvailable(paymentMethod, isApplePayAvailable),
    )
    .filter(
      (paymentMethod) =>
        paymentMethod.context ===
        (cart.orderType === OrderTypes.pickup
          ? PaymentMethodContext.pickup
          : PaymentMethodContext.delivery),
    );

  const paymentMethod = selectablePaymentMethods.find(
    (pm) => pm.id === value?.paymentMethodId,
  );

  const paysWith = value?.paysWith ?? "exact_amount";

  const cashOptions = getCashOptions(total);

  //handlers
  const handleSelectPaymentMethod = (paymentMethodId: string) => {
    onChange({ paymentMethodId, paysWith });
  };

  const handleSelectPaysWith = (nextPaysWith: string) => {
    if (!value?.paymentMethodId) return;
    onChange({
      paymentMethodId: value.paymentMethodId,
      paysWith: nextPaysWith,
    });
  };

  return (
    <div
      className="flex flex-col gap-3 p-4"
      data-error-anchor={hasError || undefined}
    >
      {hasError && (
        <p className="rounded-lg bg-red-50 p-3 text-sm font-medium text-red-600">
          {t("Checkout.PaymentData.payment_method_required")}
        </p>
      )}

      <div className="flex flex-col gap-2">
        {selectablePaymentMethods.map((selectablePaymentMethod) => (
          <PaymentMethodCard
            key={selectablePaymentMethod.id}
            paymentMethod={selectablePaymentMethod}
            fee={getPaymentMethodFee(selectablePaymentMethod, feeBase)}
            isSelected={value?.paymentMethodId === selectablePaymentMethod.id}
            onSelect={handleSelectPaymentMethod}
            className={cn(hasError && "border-red-300")}
          />
        ))}
      </div>

      {paymentMethod?.type === PaymentMethods.ideal && (
        <div className="text-muted-foreground rounded-lg border bg-amber-200 p-4">
          <span>{t("Checkout.PaymentData.ideal_disclaimer")}</span>
        </div>
      )}
      {paymentMethod?.type === PaymentMethods.applepay && (
        <div className="text-muted-foreground rounded-lg border bg-amber-200 p-4">
          <span>{t("Checkout.PaymentData.applepay_disclaimer")}</span>
        </div>
      )}
      {paymentMethod?.type === PaymentMethods.cash && (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium" htmlFor="pays-with">
            {t("Checkout.PaymentData.pays_with")}
          </label>
          <Select value={paysWith} onValueChange={handleSelectPaysWith}>
            <SelectTrigger className="w-full" id="pays-with">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="exact_amount">
                <Trans
                  i18nKey="Checkout.PaymentData.cash_exact_amount"
                  values={{
                    amount: formatNumberToCurrency({ number: total }),
                  }}
                />
              </SelectItem>
              {cashOptions.map((amount) => (
                <SelectItem key={amount} value={amount.toString()}>
                  {formatNumberToCurrency({ number: amount })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-muted-foreground rounded-lg border bg-amber-200 p-4">
            <span>{t("Checkout.PaymentData.cash_disclaimer_delivery")}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const OrderCommentForm = ({
  value,
  onChange,
}: {
  value?: string;
  onChange: (data: string) => void;
}) => {
  const customerDataForm = useForm({
    schema: z.object({
      comment: z.string().min(1),
    }),
    defaultValues: {
      comment: value ?? "",
    },
  });

  const { t } = useTranslation("translation");

  const handleSubmit = customerDataForm.handleSubmit((data) => {
    onChange(data.comment);
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit();
  };

  return (
    <Form {...customerDataForm}>
      <form
        id="requested-time"
        onSubmit={handleFormSubmit}
        className="flex flex-col gap-4"
      >
        <FormField
          control={customerDataForm.control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("Checkout.OrderComment.order_comment_label")}
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t(
                    "Checkout.OrderComment.order_comment_placeholder",
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-4">
          <button
            type="submit"
            className="rounded-full bg-black/90 cursor-pointer hover:bg-black flex-1 py-2 px-4 text-white font-bold text-lg"
          >
            {t("save")}
          </button>
          {value && (
            <button
              onClick={() => onChange("")}
              className="rounded-full bg-gray-500/90 cursor-pointer hover:bg-gray-500 flex-1 py-2 px-4 text-white font-bold text-lg"
            >
              {t("delete")}
            </button>
          )}
        </div>
      </form>
    </Form>
  );
};

const OrderCommentDialog = ({
  value,
  onChange,
  hasError = false,
}: {
  value?: string;
  onChange: (data: string) => void;
  hasError?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  const { t } = useTranslation("translation");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <CheckoutRow
          icon={StickyNoteIcon}
          label={t("Checkout.OrderComment.order_comment")}
          isComplete={!!value}
          value={value}
          errorMessage={hasError ? t("Checkout.field_invalid") : undefined}
        />
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Checkout.OrderComment.order_comment")}</DialogTitle>
          <VisuallyHidden>
            <DialogDescription></DialogDescription>
          </VisuallyHidden>
        </DialogHeader>
        <OrderCommentForm
          value={value}
          onChange={(data) => {
            onChange(data);
            setOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

const CouponForm = ({
  value,
  onChange,
  itemsTotal,
}: {
  value?: { code: string; coupon?: any };
  onChange: (data: { code: string; coupon?: any } | null) => void;
  itemsTotal: number;
}) => {
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation("translation");

  const couponForm = useForm({
    schema: z.object({
      code: z.string().min(1),
    }),
    defaultValues: {
      code: value?.code ?? "",
    },
  });

  const handleApplyCoupon = async (code: string) => {
    if (!code || code.trim() === "") {
      setError(null);
      onChange(null);
      return;
    }

    setIsChecking(true);
    setError(null);

    try {
      const result = await checkCoupon({ data: { code } });

      if (!result.success) {
        let errorMessage = t("Checkout.Coupon.invalid");
        switch (result.code) {
          case "coupon_not_found":
            errorMessage = t("Checkout.Coupon.not_found");
            break;
          case "coupon_expired":
            errorMessage = t("Checkout.Coupon.expired");
            break;
          case "coupon_not_yet_valid":
            errorMessage = t("Checkout.Coupon.not_yet_valid");
            break;
          case "coupon_usage_limit_reached":
            errorMessage = t("Checkout.Coupon.usage_limit_reached");
            break;
          case "coupon_configuration_error":
            errorMessage = t("Checkout.Coupon.configuration_error");
            break;
        }
        setError(errorMessage);
        onChange(null);
        return;
      }

      // Check minimum cart value if applicable
      if (
        result.coupon?.minCartValue &&
        itemsTotal < result.coupon.minCartValue / 100
      ) {
        setError(
          t("Checkout.Coupon.min_cart_value_not_met", {
            amount: formatNumberToCurrency({
              number: result.coupon.minCartValue / 100,
            }),
          }),
        );
        onChange(null);
        return;
      }

      setError(null);
      if (result.coupon) {
        // Carry the stored code rather than what was typed, so the cart holds a
        // value that re-checks cleanly when the order is placed.
        onChange({ code: result.coupon.code, coupon: result.coupon });
      } else {
        onChange(null);
      }
    } catch (err) {
      setError(t("Checkout.Coupon.invalid"));
      onChange(null);
    } finally {
      setIsChecking(false);
    }
  };

  const handleSubmit = couponForm.handleSubmit((data) => {
    handleApplyCoupon(data.code);
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit();
  };

  return (
    <Form {...couponForm}>
      <form
        id="coupon-form"
        onSubmit={handleFormSubmit}
        className="flex flex-col gap-4"
      >
        <FormField
          control={couponForm.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Checkout.Coupon.title")}</FormLabel>
              <FormControl>
                <div className="flex gap-2">
                  <Input
                    placeholder={t("Checkout.Coupon.placeholder")}
                    {...field}
                    disabled={isChecking}
                    className="flex-1"
                  />
                  <Button
                    type="submit"
                    disabled={isChecking || !field.value}
                    className="rounded-full bg-black/90 hover:bg-black"
                  >
                    {isChecking ? t("loading") : t("Checkout.Coupon.apply")}
                  </Button>
                </div>
              </FormControl>
              {error && <span className="text-sm text-red-500">{error}</span>}
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};

const CouponDialog = ({
  itemsTotal,
  hasError = false,
}: {
  itemsTotal: number;
  hasError?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation("translation");

  // Get coupon from cart store
  const { cart, setCoupon } = useCartStore(
    useShallow((state) => ({
      cart: state.cart,
      setCoupon: state.setCoupon,
    })),
  );

  const value = cart.coupon;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <CheckoutRow
          icon={TagIcon}
          label={t("Checkout.Coupon.title")}
          isComplete={!!value}
          value={
            value
              ? `${value.code}${value.coupon?.name ? ` (${value.coupon.name})` : ""}`
              : undefined
          }
          errorMessage={hasError ? t("Checkout.Coupon.invalid") : undefined}
        />
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Checkout.Coupon.title")}</DialogTitle>
          <VisuallyHidden>
            <DialogDescription></DialogDescription>
          </VisuallyHidden>
        </DialogHeader>
        <CouponForm
          value={value}
          onChange={(data) => {
            setCoupon(data ?? undefined);
            if (data) {
              setOpen(false);
            }
          }}
          itemsTotal={itemsTotal}
        />
        {value && (
          <Button
            variant="outline"
            onClick={() => {
              setCoupon(undefined);
              setOpen(false);
            }}
            className="w-full"
          >
            <XIcon className="size-4 mr-2" />
            {t("Checkout.Coupon.remove")}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

const OrderItemsDialog = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);

  const { t } = useTranslation("translation");

  //global state
  const { cart } = useCartStore(
    useShallow((state) => ({
      cart: state.cart,
    })),
  );

  //queries
  const { data: productsByCategory } = useMenuQuery();

  //computed
  const enabledItems = getEnabledAndAvailableItems(
    cart.items,
    productsByCategory,
  );

  const findProduct = (productId: string): Product | undefined =>
    productsByCategory
      ?.flatMap((category) => category.products)
      .find((p) => p.id === productId);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="flex flex-col p-0 gap-0 overflow-hidden max-h-[85dvh]">
        <DialogHeader className="p-4 border-b shrink-0">
          <div className="flex min-h-9 items-center pr-9">
            <DialogTitle>{t("Checkout.order_summary")}</DialogTitle>
          </div>
          <VisuallyHidden>
            <DialogDescription></DialogDescription>
          </VisuallyHidden>
        </DialogHeader>

        <div className="flex flex-col divide-y overflow-auto flex-1">
          {enabledItems.map((item) => {
            const product = findProduct(item.productId);
            const optionNames = getSelectedOptionNames(
              product,
              item.selectedOptions,
            );
            const optionsPrice = getSelectedOptionsTotalPrice(
              product,
              item.selectedOptions,
            );
            const lineTotal = (item.price + optionsPrice) * item.quantity;

            return (
              <div key={item.id} className="flex flex-col gap-1 p-4 text-left">
                <div className="flex flex-row items-start justify-between gap-4">
                  <span className="font-medium">
                    <span className="text-muted-foreground">
                      {item.quantity}&times;{" "}
                    </span>
                    {item.name}
                  </span>
                  <span className="font-medium whitespace-nowrap">
                    {formatNumberToCurrency({ number: lineTotal })}
                  </span>
                </div>
                {optionNames.length > 0 && (
                  <ul className="text-sm text-muted-foreground">
                    {optionNames.map((name, index) => (
                      <li key={`${item.id}-${index}`}>{name}</li>
                    ))}
                  </ul>
                )}
                {item.comment && (
                  <p className="text-sm italic text-muted-foreground">
                    &ldquo;{item.comment}&rdquo;
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

function CheckoutRoute() {
  const { data: websiteConfig } = useSuspenseQuery(websiteConfigQueryOptions());

  return websiteConfig.channel === SalesChannels.storefront ? (
    <StorefrontCheckout />
  ) : (
    <CheckoutPage />
  );
}

function CheckoutPage() {
  const locale = Route.useParams().locale;

  // Fall back to pickup (take away) when the business has no delivery shifts,
  // covering a returning customer whose persisted cart still holds delivery.
  useEnsureValidOrderType();

  //global state
  const { cart, setDeliveryFee } = useCartStore(
    useShallow((state) => ({
      cart: state.cart,
      setDeliveryFee: state.setDeliveryFee,
    })),
  );

  // Get coupon from cart store
  const couponValue = cart.coupon;
  const { customer } = useCustomerStore(
    useShallow((state) => ({
      customer: state.customer,
    })),
  );

  //local state
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  //queries
  const { data: paymentMethods } = useSuspenseQuery(
    paymentMethodsQueryOptions(),
  );
  const { data: productsByCategory } = useMenuQuery();

  const availabilityQuery = useSuspenseQuery(availabilityQueryOptions(TODAY));

  const availability = availabilityQuery.data?.data ?? [];

  const firstAvailableSlot = availability.find(
    (availability) =>
      availability.type === cart.orderType && availability.spots > 0,
  );

  const asapStartsAt = getAsapStartWhenClosed(
    availability,
    cart.orderType,
    new Date(),
  );

  const { data: deliveryArea } = useSuspenseQuery(
    deliveryAreaQueryOptions(
      customer.zipCode,
      customer.streetNumber,
      getSessionId(customer.zipCode, customer.streetNumber),
    ),
  );

  //translations
  const { t } = useTranslation("translation");

  //hooks
  const navigate = useNavigate();
  const { getTranslatedOrderType } = useGetTranslatedOrderType();
  const { showAlert } = useAlert();
  const isApplePayAvailable = useApplePayAvailable();

  const checkoutForm = useForm({
    schema: createOrderSchema,
    defaultValues: {
      orderType: cart.orderType,
      language: locale as unknown as SupportedLanguages,
      items: cart.items
        .filter((item) => item.enabled !== false)
        .map((item) => ({
          productId: item.productId,
          comment: item.comment,
          quantity: item.quantity,
        })),
      customer: {
        firstName: customer.firstName,
        lastName: customer.lastName,
        phone: customer.phone,
        email: customer.email,
      },
      address: {
        streetName: customer.streetName,
        streetNumber: customer.streetNumber,
        zipCode: customer.zipCode,
        city: customer.city,
      },
      requestedTime: "ASAP",
    },
  });

  const paymentMethodId = useWatch({
    control: checkoutForm.control,
    name: "payment.paymentMethodId",
  });

  const requestedTime = useWatch({
    control: checkoutForm.control,
    name: "requestedTime",
  });

  const selectedTimeslot = useWatch({
    control: checkoutForm.control,
    name: "timeslot",
  });

  const customerValues = useWatch({
    control: checkoutForm.control,
    name: "customer",
  });

  const addressValue = useWatch({
    control: checkoutForm.control,
    name: "address",
  });

  //computed
  const filteredPaymentMethods = paymentMethods.filter((paymentMethod) =>
    isPaymentMethodAvailable(paymentMethod, isApplePayAvailable),
  );
  const paymentMethod = filteredPaymentMethods.find(
    (pm) => pm.id === paymentMethodId,
  );

  // Filter to only enabled and available items for calculations
  const enabledItems = getEnabledAndAvailableItems(
    cart.items,
    productsByCategory,
  );

  const cartItemsQuantity = enabledItems.reduce(
    (acc, item) => acc + item.quantity,
    0,
  );

  // Detect cart items whose daily availability window does not contain the
  // selected timeslot. Used to warn the user before they try to submit.
  const referenceTime = selectedTimeslot ?? firstAvailableSlot?.start;
  const itemsOutsideTimeWindow = referenceTime
    ? enabledItems
        .map((item) => {
          const product = productsByCategory
            ?.flatMap((category) => category.products)
            .find((p) => p.id === item.productId);
          if (!product) return null;
          const before =
            !!product.availableFromTime &&
            referenceTime < product.availableFromTime;
          const after =
            !!product.availableUntilTime &&
            referenceTime > product.availableUntilTime;
          if (!before && !after) return null;
          return product;
        })
        .filter((p): p is Product => p !== null)
    : [];

  // Calculate itemsTotal including option prices
  const itemsTotal = enabledItems.reduce((acc, item) => {
    const product = productsByCategory
      ?.flatMap((category) => category.products)
      .find((p) => p.id === item.productId);

    const basePrice = item.price;
    const optionsPrice = getSelectedOptionsTotalPrice(
      product,
      item.selectedOptions,
    );
    const itemTotal = (basePrice + optionsPrice) * item.quantity;

    return acc + itemTotal;
  }, 0);

  const deliveryFee =
    cart.orderType === OrderTypes.pickup
      ? 0
      : getDeliveryFee(
          deliveryArea?.deliveryArea?.fee ?? 0,
          deliveryArea?.deliveryArea?.freeDeliveryFrom,
          itemsTotal,
        );

  // Calculate coupon discount
  let couponDiscount = 0;
  if (couponValue?.coupon) {
    if (couponValue.coupon.type === "cart_adjustment") {
      const subtotalBeforeDiscount = itemsTotal;
      if (couponValue.coupon.discountType === "percentage") {
        couponDiscount = new Decimal(subtotalBeforeDiscount)
          .mul(couponValue.coupon.discountValue)
          .div(100)
          .toNumber();
      } else if (couponValue.coupon.discountType === "fixed") {
        couponDiscount = couponValue.coupon.discountValue / 100;
      }
    } else if (couponValue.coupon.type === "product_adjustment") {
      // Filter eligible items based on filterType (only enabled items)
      const eligibleItems = cart.items.filter((item) => {
        // Only consider enabled items
        if (item.enabled === false) return false;

        const product = productsByCategory
          ?.flatMap((category) => category.products)
          .find((p) => p.id === item.productId);

        if (!product) return false;

        const filterType = couponValue.coupon.filterType;
        if (filterType === "all_products") {
          return true;
        } else if (filterType === "selected_products") {
          return couponValue.coupon.productIds?.includes(item.productId);
        } else if (filterType === "products_in_category") {
          const productCategoryId = product.productCategoryId;
          return couponValue.coupon.categoryIds?.includes(productCategoryId);
        }
        return false;
      });

      // Calculate total for eligible items
      const eligibleItemsTotal = eligibleItems.reduce((acc, item) => {
        const product = productsByCategory
          ?.flatMap((category) => category.products)
          .find((p) => p.id === item.productId);
        const basePrice = item.price;
        const optionsPrice = getSelectedOptionsTotalPrice(
          product,
          item.selectedOptions,
        );
        const itemTotal = (basePrice + optionsPrice) * item.quantity;
        return acc + itemTotal;
      }, 0);

      // Apply discount to eligible items
      if (couponValue.coupon.discountType === "percentage") {
        couponDiscount = new Decimal(eligibleItemsTotal)
          .mul(couponValue.coupon.discountValue)
          .div(100)
          .toNumber();
      } else if (couponValue.coupon.discountType === "fixed") {
        couponDiscount = couponValue.coupon.discountValue / 100;
        // Cap the discount at the eligible items total
        if (couponDiscount > eligibleItemsTotal) {
          couponDiscount = eligibleItemsTotal;
        }
      }
    }
  }

  // A payment method can charge a fixed surcharge, a percentage of the order,
  // or both. The percentage runs over the products plus the delivery fee, the
  // same base the server charges it over when the order is placed.
  const paymentFee = paymentMethod
    ? getPaymentMethodFee(paymentMethod, itemsTotal + deliveryFee)
    : 0;

  const total = itemsTotal + deliveryFee + paymentFee - couponDiscount;

  const freeDeliveryFrom = deliveryArea?.deliveryArea?.freeDeliveryFrom;
  const showFreeDeliveryMessage =
    cart.orderType === OrderTypes.delivery &&
    freeDeliveryFrom !== undefined &&
    itemsTotal < freeDeliveryFrom;
  const remainingAmount = showFreeDeliveryMessage
    ? freeDeliveryFrom - itemsTotal
    : 0;

  const isAvailable =
    availability.filter((a) => a.type === cart.orderType && a.spots > 0)
      .length > 0;

  // Calculate itemsTotal in cents for comparison with minOrderAmount (which is in cents)
  const itemsTotalInCents = new Decimal(itemsTotal)
    .mul(100)
    .toDecimalPlaces(0)
    .toNumber();

  // Check if minimum order amount is met (only for delivery orders)
  const minOrderAmount = deliveryArea?.deliveryArea?.minOrderAmount;
  const isMinOrderAmountMet =
    cart.orderType !== OrderTypes.delivery ||
    !minOrderAmount ||
    itemsTotalInCents >= minOrderAmount;

  // Per-step completion, so every section can show whether it still needs the
  // customer's attention instead of only revealing that after a failed submit.
  const isDeliveryOrder = cart.orderType === OrderTypes.delivery;

  const isCustomerDataComplete =
    !!customerValues?.firstName &&
    !!customerValues?.phone &&
    !!customerValues?.email;
  const isAddressComplete = !!addressValue?.streetName;
  const isRequestedTimeComplete = !!requestedTime && !!firstAvailableSlot;
  const isPaymentComplete = !!paymentMethodId;

  const requestedTimeLabel = isDeliveryOrder
    ? t("Checkout.RequestedTime.delivery_time")
    : t("Checkout.RequestedTime.pickup_time");

  // The section numbers shift when the address step is not shown at all.
  const addressStep = isDeliveryOrder ? 2 : 0;
  const timeStep = isDeliveryOrder ? 3 : 2;
  const paymentStep = isDeliveryOrder ? 4 : 3;
  const extrasStep = isDeliveryOrder ? 5 : 4;

  // Shown next to the order button so people see what is left before they press
  // it, rather than discovering it from a validation error afterwards.
  const outstandingSectionLabels = [
    !isCustomerDataComplete && t("Checkout.CustomerData.title"),
    isDeliveryOrder && !isAddressComplete && t("Checkout.AddressData.title"),
    !isRequestedTimeComplete && requestedTimeLabel,
    !isPaymentComplete && t("Checkout.PaymentData.payment_method"),
  ].filter((label): label is string => !!label);

  const formErrors = checkoutForm.formState.errors;
  const hasFormErrors = Object.keys(formErrors).length > 0;
  // Zod's own messages come from the schema we share with the server, so they
  // are English only. Name the sections in the customer's language instead.
  const missingSectionLabels = [
    formErrors.customer && t("Checkout.CustomerData.title"),
    formErrors.address && t("Checkout.AddressData.title"),
    (formErrors.requestedTime || formErrors.timeslot) && requestedTimeLabel,
    formErrors.payment && t("Checkout.PaymentData.payment_method"),
  ].filter((label): label is string => !!label);

  //handlers
  const handleSubmit = checkoutForm.handleSubmit(async (data) => {
    if (isCreatingOrder || !isAvailable) return;
    setIsCreatingOrder(true);
    try {
      let sessionId = "";
      if (data.address) {
        sessionId = getSessionId(
          data.address.zipCode,
          data.address.streetNumber,
        );
      }
      const result = await createOrder({ data: { ...data, sessionId } });

      if (result.code) {
        setIsCreatingOrder(false);

        switch (result.code) {
          case "product_no_longer_available":
            showAlert({
              title: t("Checkout.Errors.product_no_longer_available.title"),
              description: t(
                "Checkout.Errors.product_no_longer_available.description",
                {
                  product_names: result.productNames.join(", "),
                },
              ),
              variant: "destructive",
            });
            break;
          case "product_not_yet_available":
            showAlert({
              title: t("Checkout.Errors.product_not_yet_available.title"),
              description: t(
                "Checkout.Errors.product_not_yet_available.description",
                {
                  product_names: result.productNames.join(", "),
                },
              ),
              variant: "destructive",
            });
            break;
          case "products_temporarily_unavailable":
            showAlert({
              title: t(
                "Checkout.Errors.products_temporarily_unavailable.title",
              ),
              description: t(
                "Checkout.Errors.products_temporarily_unavailable.description",
                {
                  product_names: result.productNames.join(", "),
                },
              ),
              variant: "destructive",
            });
            break;
          case "no_mollie_configuration":
          case "no_mollie_profile_id":
            showAlert({
              title: t("Checkout.Errors.no_mollie_configuration.title"),
              description: t(
                "Checkout.Errors.no_mollie_configuration.description",
              ),
              variant: "destructive",
            });
            break;
          case "address_not_in_delivery_area":
            showAlert({
              title: t("Checkout.Errors.address_not_in_delivery_area.title"),
              description: t(
                "Checkout.Errors.address_not_in_delivery_area.description",
              ),
              variant: "destructive",
            });
            break;
          case "delivery_area_unavailable_today":
            showAlert({
              title: t(
                "Checkout.Errors.delivery_area_unavailable_today.title",
              ),
              description: t(
                "Checkout.Errors.delivery_area_unavailable_today.description",
              ),
              variant: "destructive",
            });
            break;
          case "order_type_not_available":
            await availabilityQuery.refetch();
            showAlert({
              title: t("Checkout.Errors.order_type_not_available.title"),
              description: t("Checkout.no_availability", {
                order_type: getTranslatedOrderType(cart.orderType).toLowerCase(),
              }),
              variant: "destructive",
            });
            break;
          case "timeslot_no_longer_available":
            // The picked time fell inside the preparation window while the
            // customer was still checking out. Reset to ASAP and pull fresh
            // slots so they can pick a time that can still be prepared.
            checkoutForm.setValue("requestedTime", "ASAP");
            await availabilityQuery.refetch();
            showAlert({
              title: t("Checkout.Errors.timeslot_no_longer_available.title"),
              description: t(
                "Checkout.Errors.timeslot_no_longer_available.description",
              ),
              variant: "destructive",
            });
            break;
          case "min_order_amount_not_met":
            showAlert({
              title: t("Checkout.Errors.min_order_amount_not_met.title"),
              description: t(
                "Checkout.Errors.min_order_amount_not_met.description",
                {
                  amount: formatNumberToCurrency({
                    number: result.minOrderAmount ?? 0,
                  }),
                },
              ),
              variant: "destructive",
            });
            break;
          default:
            showAlert({
              title: "Error",
              description: "An error occurred",
              variant: "destructive",
            });
            break;
        }
      }

      if (result?.paymentUrl) {
        setIsCreatingOrder(false);
        window.location.href = result.paymentUrl;
      }

      if (result.reference && result.secret) {
        setIsCreatingOrder(false);

        navigate({
          to: "/$locale/order/$orderReference",
          params: {
            locale: locale,
            orderReference: result.reference,
          },
          search: {
            secret: result.secret,
          },
        });
      }
    } catch (error) {
      setIsCreatingOrder(false);
      showAlert({
        title: "Error",
        description: "Error",
        variant: "destructive",
      });
    }
  }, scrollToFirstError);

  useEffect(() => {
    checkoutForm.setValue("orderType", cart.orderType);
    checkoutForm.setValue(
      "items",
      cart.items
        .filter((item) => item.enabled !== false)
        .map((item) => ({
          id: item.id,
          productId: item.productId,
          comment: item.comment ?? "",
          quantity: item.quantity,
          selectedOptions: item.selectedOptions ?? {},
          menuPicks: item.menuPicks,
          // Legacy shape, only on lines saved before menu picks existed.
          menuSelections: item.menuSelections ?? {},
          menuOptionSelections: item.menuOptionSelections ?? {},
        })),
    );
    // Update coupon in form from cart store
    if (cart.coupon) {
      checkoutForm.setValue("coupon", cart.coupon);
    } else {
      checkoutForm.setValue("coupon", undefined);
    }
  }, [cart, checkoutForm]);

  // Clear address when order type changes to pickup
  useEffect(() => {
    if (cart.orderType === OrderTypes.pickup) {
      checkoutForm.setValue("address", undefined);
    }
  }, [cart.orderType, checkoutForm]);

  useEffect(() => {
    if (deliveryArea) {
      setDeliveryFee(deliveryArea.deliveryArea?.fee ?? 0);
    }
  }, [deliveryArea]);

  useEffect(() => {
    if (firstAvailableSlot && requestedTime === "ASAP") {
      checkoutForm.setValue("timeslot", firstAvailableSlot.start);
    }
  }, [firstAvailableSlot]);

  // A specific time only exists for one order type, so switching between pickup
  // and delivery (or a slot selling out) would otherwise leave the row showing a
  // time that can no longer be ordered. Fall back to the first available slot.
  useEffect(() => {
    if (!requestedTime || requestedTime === "ASAP") return;

    const isStillSelectable = availability.some(
      (slot) =>
        slot.type === cart.orderType &&
        slot.spots > 0 &&
        slot.start.toISOString() === requestedTime,
    );

    if (isStillSelectable) return;

    checkoutForm.setValue("requestedTime", "ASAP");
    if (firstAvailableSlot) {
      checkoutForm.setValue("timeslot", firstAvailableSlot.start);
    }
  }, [cart.orderType, availability, requestedTime]);

  return (
    <Form {...checkoutForm}>
      <form onSubmit={handleSubmit} className="p-4 max-w-5xl mx-auto py-12">
        <div className="flex lg:flex-row flex-col gap-4 items-start">
          <div className="flex flex-col gap-4 w-full lg:w-3/5">
            <Link
              to="/$locale/menu"
              params={{ locale: locale }}
              className="bg-black/90 cursor-pointer flex items-center justify-center relative hover:bg-black flex-1 py-2 px-4 text-white font-bold text-lg"
            >
              <ArrowLeftIcon className="absolute left-4" />
              {t("Checkout.return_to_menu")}
            </Link>
            <CheckoutSection
              step={1}
              title={t("Checkout.CustomerData.title")}
              isRequired
              isComplete={isCustomerDataComplete}
            >
              <CustomerDataFields />
            </CheckoutSection>

            {isDeliveryOrder && (
              <FormField
                control={checkoutForm.control}
                name="address"
                render={({ field, fieldState }) => (
                  <FormItem className="gap-0">
                    <CheckoutSection
                      step={addressStep}
                      title={t("Checkout.AddressData.title")}
                      isRequired
                      isComplete={isAddressComplete}
                    >
                      <AddressDataDialog
                        value={field.value}
                        onChange={field.onChange}
                        hasError={!!fieldState.error}
                      />
                    </CheckoutSection>
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={checkoutForm.control}
              name="requestedTime"
              render={({ field, fieldState }) => (
                <FormItem className="gap-0">
                  <CheckoutSection
                    step={timeStep}
                    title={requestedTimeLabel}
                    isRequired
                    isComplete={isRequestedTimeComplete}
                  >
                    <RequestedTimeDialog
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        if (value !== "ASAP") {
                          checkoutForm.setValue("timeslot", new Date(value));
                        }
                      }}
                      asapStartsAt={asapStartsAt}
                      hasError={!!fieldState.error || !!formErrors.timeslot}
                    />
                    {asapStartsAt && field.value === "ASAP" && (
                      <div className="flex flex-row gap-3 border-t border-amber-200 bg-amber-50 p-4">
                        <ClockIcon className="size-5 shrink-0 text-amber-600" />
                        <span className="text-sm font-medium text-amber-800">
                          {isDeliveryOrder
                            ? t("Checkout.RequestedTime.starts_later_delivery", {
                                time: formatSlotTime(asapStartsAt),
                              })
                            : t("Checkout.RequestedTime.starts_later_pickup", {
                                time: formatSlotTime(asapStartsAt),
                              })}
                        </span>
                      </div>
                    )}
                  </CheckoutSection>
                </FormItem>
              )}
            />

            <FormField
              control={checkoutForm.control}
              name="payment"
              render={({ field, fieldState }) => (
                <FormItem className="gap-0">
                  <CheckoutSection
                    step={paymentStep}
                    title={t("Checkout.payment_options")}
                    isRequired
                    isComplete={isPaymentComplete}
                  >
                    <PaymentMethodSelector
                      total={total}
                      feeBase={itemsTotal + deliveryFee}
                      value={field.value}
                      onChange={field.onChange}
                      hasError={!!fieldState.error}
                    />
                  </CheckoutSection>
                </FormItem>
              )}
            />

            <CheckoutSection step={extrasStep} title={t("Checkout.extras")}>
              <div className="flex flex-col">
                <FormField
                  control={checkoutForm.control}
                  name="comment"
                  render={({ field, fieldState }) => (
                    <FormItem className="gap-0 border-b">
                      <OrderCommentDialog
                        value={field.value}
                        onChange={field.onChange}
                        hasError={!!fieldState.error}
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  control={checkoutForm.control}
                  name="coupon"
                  render={({ fieldState }) => (
                    <FormItem className="gap-0">
                      <CouponDialog
                        itemsTotal={itemsTotal}
                        hasError={!!fieldState.error}
                      />
                    </FormItem>
                  )}
                />
              </div>
            </CheckoutSection>
          </div>

          <div className="flex flex-col flex-1 lg:sticky lg:top-0">
            <div className="rounded-lg border flex-1 flex justify-between flex-col overflow-hidden">
              <div className="pt-4 px-4">
                <h4>{t("Checkout.order_summary")}</h4>
              </div>

              <div className="flex flex-col gap-1">
                <div className="border-b p-4">
                  <OrderItemsDialog>
                    <button
                      type="button"
                      className="underline hover:no-underline cursor-pointer"
                    >
                      {cartItemsQuantity > 1 &&
                        t("Checkout.OrderSummary.show_x_items", {
                          x: cartItemsQuantity,
                        })}
                      {cartItemsQuantity === 1 &&
                        t("Checkout.OrderSummary.show_one_item")}
                    </button>
                  </OrderItemsDialog>
                </div>
                <div className="p-4">
                  <div className="flex flex-row items-center justify-between gap-4">
                    <strong className="text-sm">
                      {t("Checkout.OrderSummary.subtotal")}
                    </strong>
                    <strong className="text-sm">
                      {formatNumberToCurrency({
                        number: itemsTotal,
                      })}
                    </strong>
                  </div>
                  {paymentFee > 0 && (
                    <div className="flex flex-row items-center justify-between gap-4">
                      <span className="text-sm">
                        {t("Checkout.OrderSummary.transaction_fee")}
                      </span>
                      <span className="text-sm">
                        {formatNumberToCurrency({ number: paymentFee })}
                      </span>
                    </div>
                  )}
                  {cart.orderType === OrderTypes.delivery && (
                    <div className="flex flex-row items-center justify-between gap-4">
                      <span className="text-sm">
                        {t("Checkout.OrderSummary.delivery_fee")}
                      </span>
                      <span className="text-sm">
                        {formatNumberToCurrency({
                          number: deliveryFee,
                        })}
                      </span>
                    </div>
                  )}
                  {showFreeDeliveryMessage && (
                    <div className="flex flex-row justify-center items-center gap-2 p-2 rounded-lg shadow my-2">
                      <span className="text-sm text-orange-500">
                        {t("Cart.free_delivery_message", {
                          amount: formatNumberToCurrency({
                            number: remainingAmount,
                          }),
                        })}
                      </span>
                    </div>
                  )}
                  {couponDiscount > 0 && (
                    <div className="flex flex-row items-center justify-between gap-4">
                      <span className="text-sm text-green-600">
                        {t("Checkout.OrderSummary.coupon_discount")}
                        {couponValue?.code && ` (${couponValue.code})`}
                      </span>
                      <span className="text-sm text-green-600 font-semibold">
                        -{formatNumberToCurrency({ number: couponDiscount })}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-row items-center justify-between gap-4 border-t pt-2">
                    <strong className="text-sm">
                      {t("Checkout.OrderSummary.total")}
                    </strong>
                    <strong className="text-sm">
                      {formatNumberToCurrency({ number: total })}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="flex flex-col p-4">
                {hasFormErrors && (
                  <div className="mb-4 flex flex-row gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
                    <AlertTriangleIcon className="size-5 shrink-0 text-red-600" />
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-bold text-red-700">
                        {t("Checkout.complete_required_fields")}
                      </span>
                      {missingSectionLabels.length > 0 && (
                        <ul className="list-inside list-disc text-sm text-red-700">
                          {missingSectionLabels.map((label) => (
                            <li key={label}>{label}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}

                {!hasFormErrors && outstandingSectionLabels.length > 0 && (
                  <div className="mb-4 flex flex-row gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <AlertTriangleIcon className="size-5 shrink-0 text-amber-600" />
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-bold text-amber-800">
                        {t("Checkout.still_to_complete")}
                      </span>
                      <ul className="list-inside list-disc text-sm text-amber-800">
                        {outstandingSectionLabels.map((label) => (
                          <li key={label}>{label}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {!isMinOrderAmountMet && minOrderAmount && (
                  <div className="flex flex-row justify-center items-center gap-2 p-2 rounded-lg my-2 shadow text-center bg-yellow-100">
                    <span className="text-base text-gray-800">
                      <Trans
                        i18nKey="Cart.minimum_order_amount_message"
                        values={{
                          amount: formatNumberToCurrency({
                            number: new Decimal(minOrderAmount)
                              .div(100)
                              .toNumber(),
                          }),
                        }}
                        components={{
                          amount: <span className="font-bold" />,
                        }}
                      />
                    </span>
                  </div>
                )}

                <button
                  disabled={
                    isCreatingOrder ||
                    !isAvailable ||
                    !isMinOrderAmountMet ||
                    enabledItems.length === 0 ||
                    itemsOutsideTimeWindow.length > 0
                  }
                  type="submit"
                  className="rounded-full disabled:opacity-50 disabled:cursor-not-allowed mb-4 w-full bg-black/90 cursor-pointer hover:bg-black flex-1 py-2 px-4 text-white font-bold text-lg"
                >
                  {paymentMethod?.type === PaymentMethods.ideal ||
                  paymentMethod?.type === PaymentMethods.paypal ||
                  paymentMethod?.type === PaymentMethods.applepay ||
                  paymentMethod?.type === PaymentMethods.creditcard
                    ? t("Checkout.order_and_pay")
                    : t("Checkout.order")}
                </button>
                {!isAvailable && (
                  <span className="text-red-500 text-center text-sm py-2">
                    {t("Checkout.no_availability", {
                      order_type: getTranslatedOrderType(
                        cart.orderType,
                      ).toLowerCase(),
                    })}
                  </span>
                )}
                {enabledItems.length === 0 && (
                  <span className="text-red-500 text-center text-sm py-2">
                    {t("Checkout.no_active_items")}
                  </span>
                )}
                {itemsOutsideTimeWindow.length > 0 && (
                  <div className="flex flex-row items-center gap-2 p-3 my-2 rounded-lg bg-amber-50 border border-amber-200">
                    <span className="text-sm text-amber-700">
                      {t("Checkout.timeslot_outside_window", {
                        product_names: Array.from(
                          new Set(itemsOutsideTimeWindow.map((p) => p.name)),
                        ).join(", "),
                      })}
                    </span>
                  </div>
                )}

                <p className="text-sm">
                  <Trans
                    t={t}
                    i18nKey={"Checkout.order_disclaimer"}
                    values={{
                      button_text:
                        paymentMethod?.type === PaymentMethods.ideal ||
                        paymentMethod?.type === PaymentMethods.applepay
                          ? t("Checkout.order_and_pay")
                          : t("Checkout.order"),
                    }}
                    components={{
                      privacy_policy: (
                        <a
                          className="underline hover:no-underline"
                          href={`https://www.vrodex.com/${locale}/privacy-policy`}
                        />
                      ),
                      terms: (
                        <a
                          className="underline hover:no-underline"
                          href={`https://www.vrodex.com/${locale}/terms`}
                        />
                      ),
                    }}
                  />
                </p>
              </div>
            </div>
          </div>
        </div>
        {isCreatingOrder && (
          <LoadingSpinner
            className="fixed inset-0 z-[10000]"
            text={t("Checkout.creating_order")}
          />
        )}
      </form>
    </Form>
  );
}
